import React from 'react';
import { ArrowLeft, DollarSign, CreditCard, Clock, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

function RefundPolicy() {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Back Button */}
        <Link to="/" className="inline-flex items-center gap-2 text-orange-600 hover:text-orange-700 mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-semibold">Back to Home</span>
        </Link>

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <DollarSign className="w-8 h-8 text-orange-600" />
            <h1 className="text-3xl font-bold text-slate-900">Refund Policy</h1>
          </div>
          <p className="text-slate-600 text-sm">Last Updated: January 2026</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
          
          {/* Overview */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">Refund Overview</h2>
            <p className="text-slate-700 leading-relaxed">
              BrightBazaar.in is committed to providing timely and fair refunds for eligible returns. 
              Once we receive your returned item and verify its condition, we will process your refund 
              according to the terms outlined below.
            </p>
          </section>

          {/* Processing Time */}
          <section>
            <div className="flex items-start gap-3 mb-4">
              <Clock className="w-6 h-6 text-orange-600 mt-1" />
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-3">Refund Processing Time</h2>
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-slate-900 mb-2">Inspection Period</h3>
                    <p className="text-slate-700 text-sm">
                      Once we receive your return, our team will inspect the item within <strong>2-3 business days</strong> 
                      to ensure it meets our return policy criteria.
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-slate-900 mb-2">Refund Initiation</h3>
                    <p className="text-slate-700 text-sm">
                      After approval, refunds are initiated within <strong>24 hours</strong> to your original payment method.
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-slate-900 mb-2">Credit to Account</h3>
                    <p className="text-slate-700 text-sm">
                      Depending on your bank or payment provider, it may take an additional <strong>5-10 business days</strong> 
                      for the refund to reflect in your account.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Refund Methods */}
          <section>
            <div className="flex items-start gap-3 mb-4">
              <CreditCard className="w-6 h-6 text-orange-600 mt-1" />
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-3">Refund Methods</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  Refunds are processed to the original payment method used for purchase:
                </p>
                <ul className="space-y-3 text-slate-700">
                  <li className="flex items-start gap-3 bg-gray-50 rounded-lg p-4">
                    <span className="text-orange-600 font-bold">•</span>
                    <div>
                      <strong>Credit/Debit Card:</strong> Refunded to the same card used for payment
                    </div>
                  </li>
                  <li className="flex items-start gap-3 bg-gray-50 rounded-lg p-4">
                    <span className="text-orange-600 font-bold">•</span>
                    <div>
                      <strong>UPI:</strong> Refunded to the UPI ID used for payment
                    </div>
                  </li>
                  <li className="flex items-start gap-3 bg-gray-50 rounded-lg p-4">
                    <span className="text-orange-600 font-bold">•</span>
                    <div>
                      <strong>Net Banking:</strong> Refunded to the linked bank account
                    </div>
                  </li>
                  <li className="flex items-start gap-3 bg-gray-50 rounded-lg p-4">
                    <span className="text-orange-600 font-bold">•</span>
                    <div>
                      <strong>Cash on Delivery:</strong> Refunded via bank transfer (bank details required)
                    </div>
                  </li>
                  <li className="flex items-start gap-3 bg-gray-50 rounded-lg p-4">
                    <span className="text-orange-600 font-bold">•</span>
                    <div>
                      <strong>EMI Purchases:</strong> Refund will be credited; EMI will be cancelled with your bank
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Partial Refunds */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">Partial Refunds</h2>
            <p className="text-slate-700 leading-relaxed mb-3">
              In certain situations, only partial refunds may be granted:
            </p>
            <ul className="space-y-2 text-slate-700">
              <li className="flex items-start gap-2">
                <span className="text-orange-600 mt-1">→</span>
                <span>Items with obvious signs of use or damage not caused by BrightBazaar.in</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-600 mt-1">→</span>
                <span>Items returned more than 30 days after delivery</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-600 mt-1">→</span>
                <span>Furniture items where assembly was attempted (20% restocking fee may apply)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-600 mt-1">→</span>
                <span>Items returned without original packaging or accessories</span>
              </li>
            </ul>
          </section>

          {/* Non-Refundable Items */}
          <section>
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-red-600 mt-1" />
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-3">Non-Refundable Items</h2>
                <p className="text-slate-700 leading-relaxed mb-3">
                  The following are non-refundable:
                </p>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 mt-1">✗</span>
                    <span>Gift cards and coupons</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 mt-1">✗</span>
                    <span>Shipping and handling charges (except for defective items)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 mt-1">✗</span>
                    <span>Installation or assembly charges</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 mt-1">✗</span>
                    <span>Items marked as final sale</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Exchange Policy */}
          <section className="bg-blue-50 rounded-lg p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Exchanges</h2>
            <p className="text-slate-700 leading-relaxed">
              We only replace items if they are defective or damaged. If you need to exchange an item 
              for the same product, please contact our customer service team at 
              <strong> +91 73532 97211</strong> or email us at 
              <strong> info@brightplanetventures.com</strong>.
            </p>
          </section>

          {/* Late Refunds */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">Late or Missing Refunds</h2>
            <p className="text-slate-700 leading-relaxed mb-3">
              If you haven't received your refund within the expected timeframe:
            </p>
            <ol className="space-y-2 text-slate-700">
              <li className="flex gap-3">
                <span className="font-bold text-orange-600 shrink-0">1.</span>
                <span>Check your bank account again</span>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-orange-600 shrink-0">2.</span>
                <span>Contact your credit card company (it may take time for the refund to post)</span>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-orange-600 shrink-0">3.</span>
                <span>Contact your bank (processing time varies by institution)</span>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-orange-600 shrink-0">4.</span>
                <span>If you've done all of this and still haven't received your refund, contact us</span>
              </li>
            </ol>
          </section>

          {/* Contact Section */}
          <section className="border-t pt-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Questions About Refunds?</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              Our customer support team is here to help with any refund-related queries:
            </p>
            <div className="space-y-2 text-sm">
              <p className="text-slate-700"><strong>Phone:</strong> +91 73532 97211</p>
              <p className="text-slate-700"><strong>Email:</strong> info@brightplanetventures.com</p>
              <p className="text-slate-700"><strong>Hours:</strong> Monday - Saturday, 9:00 AM - 6:00 PM IST</p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
}

export default RefundPolicy;
